import { useState } from 'react';
import { Button, Input } from '../../../../shared/ui';
import {
  UserProperty,
  CreatePropertyData,
  UpdatePropertyData,
} from '../../../../shared/utils';
import './PropertyAddressSection.css';

interface PropertyAddressSectionProps {
  properties: UserProperty[];
  onAdd: (data: CreatePropertyData) => Promise<void>;
  onUpdate: (id: string, data: UpdatePropertyData) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onSetPrimary: (id: string) => Promise<void>;
  loading: boolean;
}

const EMPTY_FORM: CreatePropertyData = {
  label: '',
  streetName: '',
  houseNumber: '',
  pinCode: '',
  city: '',
  state: '',
  country: '',
};

interface PropertyCardProps {
  property: UserProperty;
  onUpdate: (id: string, data: UpdatePropertyData) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onSetPrimary: (id: string) => Promise<void>;
  loading: boolean;
}

const PropertyCard = ({
  property,
  onUpdate,
  onDelete,
  onSetPrimary,
  loading,
}: PropertyCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<UpdatePropertyData>({
    label: property.label,
    streetName: property.streetName ?? '',
    houseNumber: property.houseNumber ?? '',
    pinCode: property.pinCode ?? '',
    city: property.city ?? '',
    state: property.state ?? '',
    country: property.country ?? '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    await onUpdate(property.id, formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      label: property.label,
      streetName: property.streetName ?? '',
      houseNumber: property.houseNumber ?? '',
      pinCode: property.pinCode ?? '',
      city: property.city ?? '',
      state: property.state ?? '',
      country: property.country ?? '',
    });
    setIsEditing(false);
  };

  return (
    <div
      className={`address-section__card ${property.isPrimary ? 'address-section__card--primary' : ''}`}
    >
      <div className="address-section__card-header">
        <div className="address-section__card-label">
          <span className="address-section__label-text">{property.label}</span>
          {property.isPrimary && (
            <span className="address-section__primary-badge">Primary</span>
          )}
        </div>
        <div className="address-section__card-actions">
          {!property.isPrimary && !isEditing && (
            <Button
              variant="text"
              size="small"
              onClick={() => onSetPrimary(property.id)}
              disabled={loading}
            >
              Set Primary
            </Button>
          )}
          {!isEditing && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setIsEditing(true)}
                disabled={loading}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => onDelete(property.id)}
                disabled={loading}
              >
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {isEditing ? (
        <form onSubmit={handleSave} className="address-section__form">
          <Input
            label="Label"
            name="label"
            value={formData.label ?? ''}
            onChange={handleChange}
            placeholder="e.g. Main Home, Holiday Flat"
            required
            fullWidth
          />
          <div className="address-section__form-row">
            <Input
              label="Street Name"
              name="streetName"
              value={formData.streetName ?? ''}
              onChange={handleChange}
            />
            <Input
              label="House Number"
              name="houseNumber"
              value={formData.houseNumber ?? ''}
              onChange={handleChange}
            />
          </div>
          <div className="address-section__form-row">
            <Input
              label="PIN Code"
              name="pinCode"
              value={formData.pinCode ?? ''}
              onChange={handleChange}
            />
            <Input
              label="City"
              name="city"
              value={formData.city ?? ''}
              onChange={handleChange}
            />
          </div>
          <div className="address-section__form-row">
            <Input
              label="State"
              name="state"
              value={formData.state ?? ''}
              onChange={handleChange}
            />
            <Input
              label="Country"
              name="country"
              value={formData.country ?? ''}
              onChange={handleChange}
            />
          </div>
          <div className="address-section__form-actions">
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" size="small" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      ) : (
        <div className="address-section__info-grid">
          <div className="address-section__info-item">
            <span className="address-section__info-label">Street Name</span>
            <span
              className={`address-section__info-value ${!property.streetName ? 'address-section__info-value--empty' : ''}`}
            >
              {property.streetName || 'Not set'}
            </span>
          </div>
          <div className="address-section__info-item">
            <span className="address-section__info-label">House Number</span>
            <span
              className={`address-section__info-value ${!property.houseNumber ? 'address-section__info-value--empty' : ''}`}
            >
              {property.houseNumber || 'Not set'}
            </span>
          </div>
          <div className="address-section__info-item">
            <span className="address-section__info-label">PIN Code</span>
            <span
              className={`address-section__info-value ${!property.pinCode ? 'address-section__info-value--empty' : ''}`}
            >
              {property.pinCode || 'Not set'}
            </span>
          </div>
          <div className="address-section__info-item">
            <span className="address-section__info-label">City</span>
            <span
              className={`address-section__info-value ${!property.city ? 'address-section__info-value--empty' : ''}`}
            >
              {property.city || 'Not set'}
            </span>
          </div>
          <div className="address-section__info-item">
            <span className="address-section__info-label">State</span>
            <span
              className={`address-section__info-value ${!property.state ? 'address-section__info-value--empty' : ''}`}
            >
              {property.state || 'Not set'}
            </span>
          </div>
          <div className="address-section__info-item">
            <span className="address-section__info-label">Country</span>
            <span
              className={`address-section__info-value ${!property.country ? 'address-section__info-value--empty' : ''}`}
            >
              {property.country || 'Not set'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export const PropertyAddressSection = ({
  properties,
  onAdd,
  onUpdate,
  onDelete,
  onSetPrimary,
  loading,
}: PropertyAddressSectionProps) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState<CreatePropertyData>(EMPTY_FORM);

  const handleAddChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAddForm(prev => ({ ...prev, [name]: value }));
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onAdd(addForm);
    setAddForm(EMPTY_FORM);
    setShowAddForm(false);
  };

  const handleAddCancel = () => {
    setAddForm(EMPTY_FORM);
    setShowAddForm(false);
  };

  return (
    <div>
      <div className="address-section__header">
        <h3 className="address-section__title">Property Addresses</h3>
        {!showAddForm && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowAddForm(true)}
            disabled={loading}
          >
            + Add Property
          </Button>
        )}
      </div>

      <div className="address-section__list">
        {properties.length === 0 && !showAddForm && (
          <p className="address-section__empty">
            No property addresses added yet.
          </p>
        )}

        {properties.map(property => (
          <PropertyCard
            key={property.id}
            property={property}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onSetPrimary={onSetPrimary}
            loading={loading}
          />
        ))}

        {showAddForm && (
          <div className="address-section__add-form">
            <p className="address-section__add-form-title">New Property</p>
            <form onSubmit={handleAddSubmit} className="address-section__form">
              <Input
                label="Label"
                name="label"
                value={addForm.label}
                onChange={handleAddChange}
                placeholder="e.g. Main Home, Holiday Flat"
                required
                fullWidth
              />
              <div className="address-section__form-row">
                <Input
                  label="Street Name"
                  name="streetName"
                  value={addForm.streetName ?? ''}
                  onChange={handleAddChange}
                />
                <Input
                  label="House Number"
                  name="houseNumber"
                  value={addForm.houseNumber ?? ''}
                  onChange={handleAddChange}
                />
              </div>
              <div className="address-section__form-row">
                <Input
                  label="PIN Code"
                  name="pinCode"
                  value={addForm.pinCode ?? ''}
                  onChange={handleAddChange}
                />
                <Input
                  label="City"
                  name="city"
                  value={addForm.city ?? ''}
                  onChange={handleAddChange}
                />
              </div>
              <div className="address-section__form-row">
                <Input
                  label="State"
                  name="state"
                  value={addForm.state ?? ''}
                  onChange={handleAddChange}
                />
                <Input
                  label="Country"
                  name="country"
                  value={addForm.country ?? ''}
                  onChange={handleAddChange}
                />
              </div>
              <div className="address-section__form-actions">
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  onClick={handleAddCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button type="submit" size="small" disabled={loading}>
                  {loading ? 'Saving...' : 'Add Property'}
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};
